import { memoSanitize } from '@axa-asia/emma-safely-set-inner-html';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import ReactModal from 'react-modal';
import TncCheckbox from './components/TncCheckbox';
import { getDialogAttributes } from '../../selectors/uiSelectors';

import { resolvePublicAssets, isExternalAssets } from '../../utils';
import { getLanguage } from '../../selectors/userSelectors';
import { openPrismicLink } from '../../actions/prismicActions';
import prismicApiClient from '../../utils/prismicApi';
import type { ExternalUrlDisclaimer } from '@axa-asia/emma-prismic-api-client';
import EmmaLoadingView from '../EmmaLoadingView';

import {
  closeDialog,
  okDialog,
  toggleHideDialog,
  otherDialog,
  putOpenEmmaTncStyleModel,
  checkRadioDialog,
} from '../../actions/uiActions';
import {
  triggerPopupDisplayScreenChange,
  triggerPopupCloseScreenChange,
} from '@axa-asia/hk-emma-web-module/lib/ga-lib/GaPreviousScreenUtil';
import './styles.scss';
import { t } from '../../locales';
import { getAvators } from '../../selectors/configSelectors';
import { Toggle } from '@axa-asia/emma-stylesheet';

interface EmmaModalViewProps {
  dialog: any;
  closeDialog: any;
  okDialog: any;
  otherDialog: any;
  toggleHideDialog: any;
  openEmmaTncStyleModel: (disclaimerId: string) => void;
  tncCount: number;
  checkRadioDialog: any;
  avator: string;
  lang: 'en' | 'zh';
  externalUrlDisclaimerUid?: string;
}

interface EmmaModalViewState {
  currentTncCount: number;
  isLoadingExternalDisclaimer: boolean;
  disclaimerDialog?: any;
}

class EmmaModalView extends Component<EmmaModalViewProps, EmmaModalViewState> {
  constructor(props: EmmaModalViewProps) {
    super(props);
    this.state = {
      currentTncCount: 0,
      isLoadingExternalDisclaimer: false,
    };
    this.checkAllRadioButton = this.checkAllRadioButton.bind(this);
    this.onCloseDialog = this.onCloseDialog.bind(this);
  }

  componentDidMount() {
    if (this.props.externalUrlDisclaimerUid) {
      this.loadExternalUrlDisclaimer();
    }
  }

  componentDidUpdate(prevProps: EmmaModalViewProps) {
    if (
      this.props.dialog &&
      prevProps.dialog &&
      this.props.dialog.visible !== prevProps.dialog.visible &&
      !this.props.dialog.visible
    ) {
      triggerPopupCloseScreenChange();
    }

    if (
      this.props.externalUrlDisclaimerUid &&
      prevProps.externalUrlDisclaimerUid !== this.props.externalUrlDisclaimerUid
    ) {
      this.loadExternalUrlDisclaimer();
    }
  }

  loadExternalUrlDisclaimer = async () => {
    const { externalUrlDisclaimerUid, lang, closeDialog } = this.props;

    if (!externalUrlDisclaimerUid) return;

    this.setState({
      isLoadingExternalDisclaimer: true,
      disclaimerDialog: this.props.dialog,
    });

    try {
      const data = await prismicApiClient
        .getExternalUrlDisclaimer({
          uid: externalUrlDisclaimerUid,
          lang: lang,
        })
        .call();

      this.setState({
        isLoadingExternalDisclaimer: false,
        disclaimerDialog: {
          ...this.props.dialog,
          modalType: 'lockedModal',
          title: data.page_title || t('EXTERNAL_URL_DISCLAIMER_TITLE'),
          contentHeader:
            data.content_header || t('EXTERNAL_URL_DISCLAIMER_CONTENT_HEADER'),
          body: data.emma_disclaimer || t('EXTERNAL_URL_DISCLAIMER_CONTENT'),
          okButtonText: data.cta_button || t('EXTERNAL_URL_DISCLAIMER_OK'),
          okAction: () => {
            if (data.cta_url) {
              openPrismicLink(
                data.cta_url, // LinkPayload object
                '', // linkQueue
                '', // linkType
                lang,
                '', // prismicBannerId
              );
              closeDialog();
            }
          },
        },
      });
    } catch (error) {
      console.error('External URL disclaimer error:', error);
      this.setState({ isLoadingExternalDisclaimer: false });
      closeDialog();
    }
  };

  checkAllRadioButton(isRadio: boolean) {
    const { currentTncCount } = this.state;
    const { checkRadioDialog } = this.props;
    const TncCount = isRadio ? currentTncCount + 1 : currentTncCount - 1;
    this.setState({
      currentTncCount: TncCount,
    });
    checkRadioDialog(isRadio);
  }

  onCloseDialog(closeAction: any) {
    const { closeDialog } = this.props;
    this.setState({
      currentTncCount: 0,
    });
    closeDialog(closeAction);
  }

  render() {
    const {
      dialog,
      closeDialog,
      okDialog,
      otherDialog,
      toggleHideDialog,
      openEmmaTncStyleModel,
      tncCount,
      avator,
      externalUrlDisclaimerUid,
    } = this.props;

    const { currentTncCount, isLoadingExternalDisclaimer, disclaimerDialog } =
      this.state;
    const isValidButton =
      tncCount == 0 || (tncCount > 0 && tncCount === currentTncCount)
        ? true
        : false;

    // Handle external URL disclaimer - override dialog content with dynamic state
    const finalDialog = externalUrlDisclaimerUid ? disclaimerDialog : dialog;

    let header,
      modalClass,
      okButton,
      closeButton,
      otherButton,
      additionBodyClass,
      tncToggles: Array<JSX.Element> = [],
      tncCheckboxes: Array<JSX.Element> = [];
    if (finalDialog) {
      const okButtonText = finalDialog.okButtonText
        ? finalDialog.okButtonText
        : 'OK';
      const otherButtonText = finalDialog.otherButtonText
        ? finalDialog.otherButtonText
        : '';

      tncToggles =
        finalDialog.tncToggles && finalDialog.tncToggles.length > 0
          ? finalDialog.tncToggles.map(
              (
                item: { label: string; title: string; tncId: string },
                index: number,
              ) => (
                <div className="emma-modal_tnc-toggle-li" key={index}>
                  <div className="emma-modal_tnc-toggle-li_label">
                    <span className="emma-modal_tnc-toggle-li_span">
                      {item.label}
                    </span>
                    <span
                      className="emma-modal_tnc-toggle-li_title"
                      onClick={() => openEmmaTncStyleModel(item.tncId)}
                      data-enable-ga
                      data-ga-type="button"
                    >
                      {item.title}
                    </span>
                  </div>
                  <div className="emma-modal_tnc-toggle-li_checkbox">
                    <Toggle
                      onChange={this.checkAllRadioButton}
                      checked={finalDialog.isCheckRadio}
                    />
                  </div>
                </div>
              ),
            )
          : [];

      tncCheckboxes =
        finalDialog.tncCheckboxes && finalDialog.tncCheckboxes.length > 0
          ? finalDialog.tncCheckboxes.map(
              (
                item: { label: string; title: string; tncId: string },
                index: number,
              ) => {
                const { label, title, tncId } = item;
                return (
                  <TncCheckbox
                    label={label}
                    title={title}
                    tncId={tncId}
                    checked={finalDialog.isCheckRadio}
                    onChange={this.checkAllRadioButton}
                    openTncStyleModal={openEmmaTncStyleModel}
                  />
                );
              },
            )
          : [];

      if (finalDialog.modalType === 'lockedModal') {
        header = (
          <div>
            <img
              className="emma-modal-locked__img"
              alt="emma-smile-glasses.png"
              src={
                isExternalAssets(avator) ? avator : resolvePublicAssets(avator)
              }
            />
            <div
              className="emma-modal-locked__text"
              dangerouslySetInnerHTML={{
                __html: memoSanitize(finalDialog.title),
              }}
            ></div>
          </div>
        );
        modalClass = 'emma-modal emma-modal-locked';
        if (finalDialog.okAction) {
          okButton = (
            <button
              className={
                isValidButton
                  ? 'emma-stylesheet-primary-default-button'
                  : 'emma-stylesheet-primary-secondary-default-disabled-button'
              }
              onClick={() => {
                if (externalUrlDisclaimerUid) {
                  finalDialog.okAction();
                } else if (isValidButton) okDialog(finalDialog.okAction);
              }}
              data-enable-ga
              data-ga-type="button"
            >
              {okButtonText}
            </button>
          );
        } else {
          okButton = (
            <button
              className={
                isValidButton
                  ? 'emma-stylesheet-primary-default-button'
                  : 'emma-stylesheet-primary-secondary-default-disabled-button'
              }
              onClick={() => {
                if (isValidButton) toggleHideDialog();
              }}
              data-enable-ga
              data-ga-type="button"
            >
              {okButtonText}
            </button>
          );
        }
        if (finalDialog.closeAction) {
          closeButton = (
            <span
              className="emma-modal-locked__close"
              onClick={() => this.onCloseDialog(finalDialog.closeAction)}
              data-enable-ga
              data-ga-type="button"
            >
              {' '}
              {t('CLOSE')}{' '}
            </span>
          );
        } else {
          closeButton = (
            <span
              className="emma-modal-locked__close"
              onClick={() => toggleHideDialog()}
              data-enable-ga
              data-ga-type="button"
            >
              {' '}
              {t('CLOSE')}{' '}
            </span>
          );
        }
        if (dialog.otherDialog && otherButtonText) {
          otherButton = (
            <button
              className="emma-stylesheet-primary-ghost-button"
              onClick={() => otherDialog(dialog.otherAction)}
              data-enable-ga
              data-ga-type="button"
            >
              {otherButtonText}
            </button>
          );
        }
      } else {
        header = <div>{dialog.title}</div>;
        modalClass = 'emma-modal';
        okButton = (
          <button
            className="emma-stylesheet-primary-default-button"
            onClick={() => okDialog(dialog.okAction)}
            data-enable-ga
            data-ga-type="button"
          >
            {okButtonText}
          </button>
        );

        if (dialog.otherDialog && otherButtonText) {
          otherButton = (
            <button
              className="emma-stylesheet-primary-ghost-button"
              onClick={() => otherDialog(dialog.otherAction)}
              data-enable-ga
              data-ga-type="button"
            >
              {otherButtonText}
            </button>
          );
        }
      }
      additionBodyClass =
        dialog.otherDialog && otherButtonText ? 'emma-modal__addition-btn' : '';
    }

    return (
      <div>
        {finalDialog && (
          <ReactModal
            isOpen={finalDialog.visible}
            contentLabel="Minimal Modal Example"
            className={modalClass}
            overlayClassName="emma-modal__overlay"
            appElement={document.getElementById('root') || undefined}
          >
            <div className="emma-modal-wrapper">
              <EmmaLoadingView isLoading={isLoadingExternalDisclaimer} />
              <h1 className="emma-modal__title">
                {header}
                {closeButton}
              </h1>
              <div className={'emma-modal__body ' + additionBodyClass}>
                {finalDialog.contentHeader && (
                  <h2>{finalDialog.contentHeader}</h2>
                )}
                <div
                  className={'emma-modal__body_content'}
                  dangerouslySetInnerHTML={{
                    __html: memoSanitize(finalDialog.body),
                  }}
                />
                {tncToggles.length > 0 && (
                  <div className="emma-modal_tnc-toggle-list">{tncToggles}</div>
                )}
                {tncCheckboxes.length > 0 && (
                  <div className="emma-modal_tnc-checkbox-list">
                    {' '}
                    {tncCheckboxes}
                  </div>
                )}
              </div>
              {!isLoadingExternalDisclaimer && (
                <div className="emma-modal__btn-wrapper">
                  {okButton}
                  {otherButton}
                </div>
              )}
            </div>
          </ReactModal>
        )}
      </div>
    );
  }

  static mapStateToProps(state: any) {
    const dialogState = Object.assign({}, getDialogAttributes(state));
    const tncCount =
      dialogState && dialogState.tncToggles && dialogState.tncToggles.length > 0
        ? dialogState.tncToggles.length
        : 0;
    const avators = getAvators(state);
    const lang = getLanguage(state);
    return {
      dialog: dialogState,
      tncCount: tncCount,
      avator: avators && avators.discliamerIcon,
      lang: lang,
      externalUrlDisclaimerUid: dialogState?.externalUrlDisclaimerUid || null,
    };
  }

  static mapDispatchToProps(dispatch: any) {
    return {
      closeDialog: (payload: any) => dispatch(closeDialog(payload)),
      okDialog: (payload: any) => dispatch(okDialog(payload)),
      otherDialog: (payload: any) => dispatch(otherDialog(payload)),
      toggleHideDialog: () => dispatch(toggleHideDialog()),
      openEmmaTncStyleModel: (disclaimerId: string) => {
        dispatch(putOpenEmmaTncStyleModel(disclaimerId, true));
      },
      checkRadioDialog: (payload: any) => dispatch(checkRadioDialog(payload)),
    };
  }
}

export default connect(
  EmmaModalView.mapStateToProps,
  EmmaModalView.mapDispatchToProps,
)(EmmaModalView);
